import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiCalendar, FiClock, FiMinimize2, FiZap, FiDownload, FiBell } = FiIcons;

export default function UpcomingEventsWidget() {
  const [events, setEvents] = useState([]);
  const [isMinimized, setIsMinimized] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    fetchEvents();
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchEvents = async () => {
    const { data } = await supabase
      .from('gallo_events_1786733905424')
      .select('*')
      .gte('scheduled_at', new Date().toISOString())
      .order('scheduled_at', { ascending: true })
      .limit(2);
    if (data) setEvents(data);
  };

  const downloadICS = (ev) => {
    const startDate = new Date(ev.scheduled_at);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours
    const formatICSDate = (d) => d.toISOString().replace(/-|:|\.\d+/g, '');

    const icsData = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Hacienda El Gallo//Transmisiones En Vivo//ES',
      'BEGIN:VEVENT',
      `UID:${ev.id}@haciendaelgallo.net`,
      `DTSTAMP:${formatICSDate(new Date())}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${ev.title}`,
      `DESCRIPTION:${ev.description || 'Transmisión oficial en vivo de Hacienda El Gallo.'}`,
      'URL:https://live.haciendaelgallo.net/',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `evento_${ev.id.slice(0, 6)}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getTimeLeft = (targetDate) => {
    const diff = new Date(targetDate) - now;
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, isLive: true };
    return {
      d: Math.floor(diff / (1000 * 60 * 60 * 24)),
      h: Math.floor((diff / (1000 * 60 * 60)) % 24),
      m: Math.floor((diff / 1000 / 60) % 60),
      s: Math.floor((diff / 1000) % 60),
      isLive: false
    };
  };

  if (events.length === 0) return null;

  if (isMinimized) {
    return (
      <button
        type="button"
        onClick={() => setIsMinimized(false)}
        className="flex items-center gap-2 rounded-full bg-gray-900/90 hover:bg-gray-850 dark:bg-black/85 dark:hover:bg-black/95 text-white px-4 py-2.5 shadow-lg border border-white/10 transition-all duration-300 transform hover:scale-105"
        title="Ver Próximos Eventos"
      >
        <SafeIcon icon={FiCalendar} size={16} className="text-red-500" />
        <span className="text-xs font-bold uppercase tracking-wider">Próximo Evento</span>
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/20 dark:border-white/10 bg-white/95 dark:bg-black/80 backdrop-blur-md p-5 space-y-4 shadow-2xl transition-all duration-300 w-full max-w-[320px]"
    >
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-600/10 text-red-600 dark:text-red-500">
            <SafeIcon icon={FiCalendar} size={14} />
          </span>
          <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Cartelera Oficial</h4>
        </div>
        <button
          type="button"
          onClick={() => setIsMinimized(true)}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <SafeIcon icon={FiMinimize2} size={14} />
        </button>
      </div>

      <div className="space-y-4">
        {events.map((ev, index) => {
          const timeLeft = getTimeLeft(ev.scheduled_at);
          const isMainEvent = index === 0;
          return (
            <div
              key={ev.id}
              className={`flex flex-col gap-3 p-3 rounded-xl border ${
                isMainEvent
                  ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30'
                  : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10'
              }`}
            >
              <div className="flex items-start gap-3">
                <img
                  src={ev.banner_url || 'https://images.unsplash.com/photo-1549417229-aa67d3263c09?auto=format&fit=crop&w=100&q=80'}
                  alt=""
                  className="w-12 h-12 object-cover rounded-lg shrink-0 border border-black/10 dark:border-white/10"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-gray-900 dark:text-white truncate drop-shadow-sm">{ev.title}</p>
                  <p className="text-[10px] text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5 leading-tight">{ev.description}</p>
                  <button
                    onClick={() => downloadICS(ev)}
                    className="mt-1 flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400 hover:underline"
                  >
                    <SafeIcon icon={FiDownload} size={10} /> Guardar en Calendario
                  </button>
                </div>
              </div>

              {isMainEvent && !timeLeft.isLive && (
                <div className="bg-white dark:bg-black/40 rounded-lg p-2 border border-gray-100 dark:border-white/5 flex justify-between items-center mt-1 shadow-sm">
                  <div className="flex flex-col items-center px-2">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200 font-mono">{String(timeLeft.d).padStart(2, '0')}</span>
                    <span className="text-[8px] text-gray-500 uppercase font-bold">Días</span>
                  </div>
                  <span className="text-gray-300 dark:text-gray-600">:</span>
                  <div className="flex flex-col items-center px-2">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200 font-mono">{String(timeLeft.h).padStart(2, '0')}</span>
                    <span className="text-[8px] text-gray-500 uppercase font-bold">Hrs</span>
                  </div>
                  <span className="text-gray-300 dark:text-gray-600">:</span>
                  <div className="flex flex-col items-center px-2">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200 font-mono">{String(timeLeft.m).padStart(2, '0')}</span>
                    <span className="text-[8px] text-gray-500 uppercase font-bold">Min</span>
                  </div>
                  <span className="text-gray-300 dark:text-gray-600">:</span>
                  <div className="flex flex-col items-center px-2">
                    <span className="text-sm font-bold text-red-600 dark:text-red-500 font-mono">{String(timeLeft.s).padStart(2, '0')}</span>
                    <span className="text-[8px] text-red-500/80 uppercase font-bold">Seg</span>
                  </div>
                </div>
              )}

              {isMainEvent && timeLeft.isLive && (
                <div className="bg-red-600 text-white rounded-lg p-2 flex justify-center items-center gap-2 mt-1 shadow-sm animate-pulse">
                  <SafeIcon icon={FiZap} size={14} />
                  <span className="text-xs font-bold uppercase tracking-wider">¡El evento ha comenzado!</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}